import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Wrench, Sparkles, AlertTriangle, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AiMaintenanceModal({ 
    open, 
    onOpenChange, 
    vehicle, 
    language,
    onComplete 
}) {
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [suggestions, setSuggestions] = useState(null);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [showAll, setShowAll] = useState(false);

    const fetchSuggestions = async () => {
        if (!vehicle?.id) return;
        
        setLoading(true);
        try {
            const response = await api.get(`/vehicles/${vehicle.id}/suggest-maintenance`);
            setSuggestions(response.data);
            // Pre-select common maintenance items
            const commonItems = ['Oil Change', 'Brake Pads', 'Air Filter', 'Timing Belt/Chain'];
            const preselected = response.data.suggestions
                .filter(s => commonItems.includes(s.type))
                .map(s => s.type);
            setSelectedTypes(preselected);
        } catch (error) {
            toast.error(language === 'el' ? 'Αποτυχία φόρτωσης προτάσεων' : 'Failed to load suggestions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && vehicle) {
            fetchSuggestions();
        }
    }, [open, vehicle]);

    const handleToggle = (type) => {
        setSelectedTypes(prev => 
            prev.includes(type) 
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const handleSelectAll = () => {
        if (suggestions) {
            setSelectedTypes(suggestions.suggestions.map(s => s.type));
        }
    };

    const handleDeselectAll = () => {
        setSelectedTypes([]);
    };

    const handleApply = async () => {
        if (selectedTypes.length === 0) {
            toast.warning(language === 'el' ? 'Επιλέξτε τουλάχιστον μία συντήρηση' : 'Select at least one maintenance item');
            return;
        }

        setApplying(true);
        try {
            const response = await api.post(`/vehicles/${vehicle.id}/apply-maintenance-suggestions`, {
                vehicle_id: vehicle.id,
                selected_types: selectedTypes
            });
            
            toast.success(
                language === 'el' 
                    ? `Δημιουργήθηκαν ${response.data.created_count} εγγραφές συντήρησης!`
                    : `Created ${response.data.created_count} maintenance records!`
            );
            
            onComplete?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(language === 'el' ? 'Αποτυχία δημιουργίας συντήρησης' : 'Failed to create maintenance records');
        } finally {
            setApplying(false);
        }
    };

    const formatInterval = (suggestion) => {
        const parts = [];
        if (suggestion.km_interval) {
            parts.push(`${suggestion.km_interval.toLocaleString()} km`);
        }
        if (suggestion.months_interval) {
            parts.push(`${suggestion.months_interval} ${language === 'el' ? 'μήνες' : 'months'}`);
        }
        return parts.join(language === 'el' ? ' ή ' : ' or ');
    };

    // Show only first 6 items unless showAll is true
    const displayedSuggestions = suggestions?.suggestions 
        ? (showAll ? suggestions.suggestions : suggestions.suggestions.slice(0, 6))
        : [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] bg-white">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-slate-800">
                        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="text-lg font-semibold">
                                {language === 'el' ? 'AI Προτάσεις Συντήρησης' : 'AI Maintenance Suggestions'}
                            </div>
                            {vehicle && (
                                <div className="text-sm font-normal text-slate-500">
                                    {vehicle.make} {vehicle.model} ({vehicle.year})
                                </div>
                            )}
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <div className="mt-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-10 h-10 text-violet-500 animate-spin mb-4" />
                            <p className="text-slate-600">
                                {language === 'el' 
                                    ? 'Αναζήτηση προγράμματος συντήρησης...' 
                                    : 'Searching maintenance schedule...'}
                            </p>
                            <p className="text-sm text-slate-400 mt-1">
                                {language === 'el' 
                                    ? 'Το AI αναλύει δεδομένα για το όχημά σας' 
                                    : 'AI is analyzing data for your vehicle'}
                            </p>
                        </div>
                    ) : suggestions ? (
                        <>
                            {/* Source note */}
                            {suggestions.is_generic && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-amber-800">
                                        <strong>{language === 'el' ? 'Σημείωση:' : 'Note:'}</strong>{' '}
                                        {language === 'el' 
                                            ? 'Αυτές είναι γενικές εκτιμήσεις. Επιβεβαιώστε με το εγχειρίδιο του οχήματος.'
                                            : 'These are generic estimates. Please verify with your vehicle manual.'}
                                    </div>
                                </div>
                            )}

                            {/* Selection controls */}
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-sm text-slate-500">
                                    {selectedTypes.length} / {suggestions.suggestions.length}{' '}
                                    {language === 'el' ? 'επιλεγμένα' : 'selected'}
                                </span>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                                        {language === 'el' ? 'Επιλογή όλων' : 'Select all'}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
                                        {language === 'el' ? 'Αποεπιλογή' : 'Deselect'}
                                    </Button>
                                </div>
                            </div>

                            {/* Suggestions list */}
                            <ScrollArea className="h-[350px]">
                                <div className="space-y-2 pr-4">
                                    {displayedSuggestions.map((suggestion) => (
                                        <div
                                            key={suggestion.type}
                                            className={`border rounded-lg p-3 cursor-pointer transition-all ${
                                                selectedTypes.includes(suggestion.type)
                                                    ? 'border-violet-300 bg-violet-50'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            onClick={() => handleToggle(suggestion.type)}
                                        >
                                            <div className="flex items-start gap-3">
                                                <Checkbox
                                                    checked={selectedTypes.includes(suggestion.type)}
                                                    onCheckedChange={() => handleToggle(suggestion.type)}
                                                    className="mt-1"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <Wrench className="w-4 h-4 text-slate-400" />
                                                        <span className="font-medium text-slate-800">
                                                            {suggestion.type_el}
                                                        </span>
                                                        {suggestion.is_estimate && (
                                                            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                                                {language === 'el' ? 'Εκτίμηση' : 'Estimate'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                                                        <span>📏 {formatInterval(suggestion)}</span>
                                                        {suggestion.estimated_cost_eur && (
                                                            <span>💶 ~€{suggestion.estimated_cost_eur}</span>
                                                        )}
                                                    </div>
                                                    {suggestion.notes && (
                                                        <p className="text-xs text-slate-400 mt-1 truncate">
                                                            💡 {suggestion.notes}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Show more/less button */}
                                    {suggestions.suggestions.length > 6 && (
                                        <Button
                                            variant="ghost"
                                            className="w-full mt-2 text-violet-600"
                                            onClick={() => setShowAll(!showAll)}
                                        >
                                            {showAll ? (
                                                <>
                                                    <ChevronUp className="w-4 h-4 mr-1" />
                                                    {language === 'el' ? 'Εμφάνιση λιγότερων' : 'Show less'}
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDown className="w-4 h-4 mr-1" />
                                                    {language === 'el' 
                                                        ? `Εμφάνιση όλων (${suggestions.suggestions.length - 6} ακόμη)` 
                                                        : `Show all (${suggestions.suggestions.length - 6} more)`}
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </ScrollArea>

                            {/* Action buttons */}
                            <div className="flex gap-3 mt-6 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => onOpenChange(false)}
                                    disabled={applying}
                                >
                                    {language === 'el' ? 'Παράλειψη' : 'Skip'}
                                </Button>
                                <Button
                                    className="flex-1 bg-violet-600 hover:bg-violet-500 text-white"
                                    onClick={handleApply}
                                    disabled={applying || selectedTypes.length === 0}
                                >
                                    {applying ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            {language === 'el' ? 'Δημιουργία...' : 'Creating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4 mr-2" />
                                            {language === 'el' 
                                                ? `Προσθήκη ${selectedTypes.length} συντηρήσεων` 
                                                : `Add ${selectedTypes.length} maintenance items`}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
}
